import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DictionaryService } from 'app/core/services/dictionary.service';
import { BehaviorSubject, catchError, lastValueFrom, map, throwError } from 'rxjs';
import { APIURL, StoreKey } from '@app/shared';
import { LoginResponse } from '../models';
import { IosDialogService } from './ios-dialog.service';

@Injectable()
export class AuthService {
  private BASEAUTHURL = APIURL + '';

  loggedStatus$ = new BehaviorSubject(this.isLoggedIn);

  constructor(
    private router: Router,
    private http: HttpClient,
    private dictionaryService: DictionaryService,
    private dialog: IosDialogService,
  ) {}

  get isLoggedIn(): boolean {
    return !!sessionStorage.getItem(StoreKey.TOKEN);
  }

  get hasRefreshToken(): boolean {
    return !!localStorage.getItem(StoreKey.REFRESHTOKEN);
  }

  private url(url: string): string {
    return this.BASEAUTHURL + url;
  }

  async redirectSSO(redirectPath?: string): Promise<{ url: string }> {
    const queryParams = redirectPath ? `?redirectPath=${redirectPath}` : '';
    const url = this.url(`ssoStartLogin${queryParams}`);
    return lastValueFrom(this.http.get<{ url: string }>(url));
  }

  async login(deviceId: string, idToken: string): Promise<LoginResponse> {
    const url = this.url('login');
    const body = { deviceId, jwt: idToken };
    return lastValueFrom(this.http.post<LoginResponse>(url, body));
  }

  logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.dialog.closeAll();
    this.dictionaryService.cleanCache();
    this.loggedStatus$.next(false);
    this.router.navigate(['/home']);
  }

  async refreshTokenSync(): Promise<void> {
    const response = await lastValueFrom(this.refreshTokenOb());
    const { token, refreshToken: newRefreshToken } = response;
    localStorage.setItem(StoreKey.REFRESHTOKEN, newRefreshToken);
    sessionStorage.setItem(StoreKey.TOKEN, token);
    this.loggedStatus$.next(true);
  }

  refreshTokenOb() {
    const deviceId = localStorage.getItem(StoreKey.DEVICEID);
    const userId = localStorage.getItem(StoreKey.CUSTOMERID);
    const refreshToken = localStorage.getItem(StoreKey.REFRESHTOKEN);
    const url = this.url('refreshToken');
    const body = { deviceId, userId, refreshToken };
    return this.http.post<{ token: string; refreshToken: string }>(url, body).pipe(
      map((response) => {
        const { token, refreshToken: newRefreshToken } = response;
        localStorage.setItem(StoreKey.REFRESHTOKEN, newRefreshToken);
        sessionStorage.setItem(StoreKey.TOKEN, token);
        this.loggedStatus$.next(true);

        return response;
      }),
      catchError((error) => {
        this.logout();
        return throwError(() => new Error(error));
      }),
    );
  }

  detachedLogin(jwt: string): Promise<{ customerName: string; offlineCustomerKey: string; customerId: string }> {
    const url = this.url('detached-login');
    return lastValueFrom(this.http.post<{ customerName: string; offlineCustomerKey: string; customerId: string }>(url, { jwt }));
  }
}
